import Vue, { CreateElement } from '@src/vue'
import { Component } from 'vue-property-decorator'

import { ExerciseResponse } from '@src/data/exercise-response'
import { ExerciseFilters } from '@src/state/store'

import { ExerciseService } from '@src/service/exercise'

import { Link } from '@src/elements/link/link'
import { Loader } from '@src/components/loading-screen/loader/loader'
import ExerciseTagsFilters from '@src/routes/content/components/exercise-tags-filters/exercise-tags-filters'
import { ExerciseCardInfo } from '@src/components/card/exercise-card/exercise-card-info/exercise-card-info'
import { Pagination } from '@src/elements/pagination/pagination'
import { Button } from '@src/elements/button/button'
import { track } from '@src/util/functions'
import { ErrorScreen } from '@src/components/error-screen/error-screen'
import { PrintExercises } from '@src/components/print/print-exercises'
import { defaultExerciseFilters } from '@src/state/dafaults'
import { DEFAULT_PAGE_SIZE } from '@src/util/constants'

import './exercises.sass'

@Component({
  computed: {
    exerciseFilters: {
      get: function () {
        return this.$store.state.exerciseFilters
      },
      set: function (filters) {
        this.$store.state.exerciseFilters = filters
      }
    },
    availableExercises () {
      return this.$store.state.availableExercises
    },
    loadAvailableExercisesComplete () {
      return this.$store.state.loadAvailableExercisesComplete
    }
  }
})
export default class Exercises extends Vue {

  private exerciseFilters: ExerciseFilters

  private availableExercises: ExerciseResponse[]
  private loadAvailableExercisesComplete: boolean

  private pageSize: number = DEFAULT_PAGE_SIZE

  private showPrintExercises: boolean = false
  private printMediaQueryList: MediaQueryList

  constructor () {
    super()
    track('/content/exercises')
  }

  created () {
    // after print, reset exercise to be printed
    window.addEventListener('afterprint', this.resetPrint)
    this.printMediaQueryList = window.matchMedia('print')
    this.printMediaQueryList.addListener(this.onPrintMediaChange)
  }

  onPrintMediaChange (mql: MediaQueryListEvent) {
    if (!mql.matches) {
      this.resetPrint()
    }
  }

  printExercises () {
    this.showPrintExercises = true
  }

  resetPrint () {
    this.showPrintExercises = false
  }

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MY_CONTENT')} - ${this.$root.$t('PAGE.CONTENT.EXERCISES.TAB_NAME')}`
  }

  getFilteredExercises () {
    // filter
    let filteredExercises = this.availableExercises.filter((exercise) =>
      ExerciseService.filterByTagsAndEquipment(exercise, this.exerciseFilters.tags || {})
      && (ExerciseService.filterByName(exercise, this.exerciseFilters.name || '') || ExerciseService.filterByNumber(exercise, this.exerciseFilters.name || /* istanbul ignore next: an empty name matches by name already */ ''))
      && ExerciseService.filterByFavorites(exercise, this.exerciseFilters.favoritesOnly || false, this.$store.state.favoriteExerciseIds)
    )

    // sort
    if (!this.exerciseFilters.name || !this.exerciseFilters.name!.trim().length) {
      return filteredExercises
    } else if (isNaN(Number(this.exerciseFilters.name))) {
      return filteredExercises.sort((a, b) => a.getName() > b.getName() ? 1 : a.getName() < b.getName() ? -1 : 0)
    } else {
      return filteredExercises.sort((a, b) => a.number > b.number ? 1 : a.number < b.number ? -1 : 0)
    }
  }

  clearFilters () {
    this.$store.dispatch('filterExercises', defaultExerciseFilters())
  }

  changePage (page: number) {
    document.getElementById('component-tabs-body')!.scrollTop = 0
    this.$store.dispatch('filterExercises', { page })
  }

  paginate (items: ExerciseResponse[]) {
    let start = (this.exerciseFilters.page - 1) * this.pageSize
    return items.slice(start, start + this.pageSize)
  }

  render (h: CreateElement) {

    const filteredExercises = this.getFilteredExercises()

    const exerciseCards = this.paginate(filteredExercises).map(exercise => {
      return <ExerciseCardInfo key={exercise.id} exercise={exercise} />
    })

    const isStillLoading = !this.loadAvailableExercisesComplete ||
      (this.availableExercises.length === 0 && !this.tagFiltersSelected() && !this.exerciseFilters.name && !this.exerciseFilters.favoritesOnly)

    return <div class='exercises-catalogue'>
      <div class='btns-row'>
        <ExerciseTagsFilters expandable={true} />
        <Button type='remove' click={this.printExercises} icon={'util/print'} label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.PRINT_QR')} class='print-btn' />
      </div>

      <div>
        {isStillLoading ?
          <Loader />

          : exerciseCards.length ?
            <div>
              <div class='exercises'>
                {exerciseCards}
              </div>
              <Pagination changePage={this.changePage}
                          currentPage={this.exerciseFilters.page}
                          pageSize={this.pageSize}
                          total={filteredExercises.length} />
            </div>

            : this.renderErrorScreen(h)}
      </div>

      {this.showPrintExercises ? <PrintExercises exercises={filteredExercises} /> : ''}
    </div>
  }

  renderErrorScreen (h: CreateElement) {
    let message: any = ''
    if (this.exerciseFilters.name) message = this.$t('PAGE.CONTENT.WORKOUTS.NOT_FOUND.FILTER.NAME', { query: this.exerciseFilters.name })
    else if (this.tagFiltersSelected() || this.exerciseFilters.favoritesOnly) message = this.$t('PAGE.CONTENT.WORKOUT.NO_EXERCISES_FILTERS')
    return <ErrorScreen message={message}>
      <Link variant='colored' align='center' click={this.clearFilters} label={this.$t('PAGE.CONTENT.WORKOUT.SHOW_ALL_EXERCISES')} />
    </ErrorScreen>
  }

  tagFiltersSelected () {
    for (let type in this.exerciseFilters.tags!) {
      if (this.exerciseFilters.tags![type].size > 0) return true
    }
    return false
  }

  beforeDestroy () {
    window.removeEventListener('afterprint', this.resetPrint)
    this.printMediaQueryList.removeListener(this.onPrintMediaChange)
    this.$store.dispatch('filterExercises', defaultExerciseFilters())
  }
}
